import { useState } from 'react';
import { useAuth } from '@clerk/react';
import { CloudDownload, CloudUpload, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import {
  countAccountBundleRows,
  restoreAccountDataBundle,
  snapshotAccountDataBundle,
} from './localAccountData';
import { downloadAccountDataBundle, uploadAccountDataBundle } from './accountDataSyncApi';
import { getAccountSyncKey } from './syncKeyStore';
import { PassphraseSession } from '@/lib/crypto';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type SyncState =
  | { kind: 'idle' }
  | { kind: 'busy'; label: string }
  | { kind: 'done'; message: string }
  | { kind: 'error'; message: string };

export function AccountDataSyncCard() {
  const { getToken, userId } = useAuth();
  const [state, setState] = useState<SyncState>({ kind: 'idle' });

  async function requireAccount(): Promise<{ token: string; accountIdentity: string }> {
    const token = await getToken();
    if (!token || !userId) throw new Error('No Clerk account session available.');
    return { token, accountIdentity: userId };
  }

  async function upload() {
    setState({ kind: 'busy', label: 'Uploading…' });
    try {
      const { token, accountIdentity } = await requireAccount();
      const syncKey = await getAccountSyncKey(token);
      PassphraseSession.setKey(syncKey, new Uint8Array(new ArrayBuffer(0)), accountIdentity);
      const bundle = await snapshotAccountDataBundle();
      await uploadAccountDataBundle(token, bundle, syncKey);
      const message = `Uploaded ${countAccountBundleRows(bundle)} local records.`;
      setState({ kind: 'done', message });
      toast.success(message);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState({ kind: 'error', message });
      toast.error(`Account sync upload failed: ${message}`);
    }
  }

  async function replaceLocalData() {
    const confirmed = window.confirm(
      'Replace local data with the latest account backup? This overwrites local favorites, history, settings, saved searches, blacklist, and source cookies. Local-only source cookies that are not in the backup will be removed.',
    );
    if (!confirmed) return;
    setState({ kind: 'busy', label: 'Replacing local data…' });
    try {
      const { token, accountIdentity } = await requireAccount();
      const syncKey = await getAccountSyncKey(token);
      const bundle = await downloadAccountDataBundle(token, syncKey);
      if (!bundle) {
        throw new Error('No account sync bundle found.');
      }
      PassphraseSession.setKey(syncKey, new Uint8Array(new ArrayBuffer(0)), accountIdentity);
      await restoreAccountDataBundle(bundle);
      const message = `Replaced local data with ${countAccountBundleRows(bundle)} account records.`;
      setState({ kind: 'done', message });
      toast.success(message);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState({ kind: 'error', message });
      toast.error(`Account sync restore failed: ${message}`);
    }
  }

  const busy = state.kind === 'busy';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Account data sync</CardTitle>
        <CardDescription>
          Signed-in devices can use the same Bunko account backup. Data is encrypted for transit and
          storage, but Bunko backend infrastructure can technically recover account sync data.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Back up again after this update to enable simplified restore on fresh signed-in devices.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => void upload()} disabled={busy}>
            <CloudUpload className="mr-1.5 h-4 w-4" />
            Back up now
          </Button>
          <Button variant="outline" onClick={() => void replaceLocalData()} disabled={busy}>
            <CloudDownload className="mr-1.5 h-4 w-4" />
            Replace local data with account backup
          </Button>
        </div>

        {state.kind === 'busy' && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <RefreshCw className="h-4 w-4 animate-spin" />
            {state.label}
          </p>
        )}
        {state.kind === 'done' && <p className="text-sm text-emerald-500">{state.message}</p>}
        {state.kind === 'error' && (
          <p role="alert" className="text-sm text-destructive">
            {state.message}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
